
import React, { useState } from 'react';
import { VendorProfile } from '../types';

interface RequestChangesModalProps {
  vendor: VendorProfile;
  onClose: () => void;
  onSend: (message: string) => void;
}

const PRESET_OPTIONS = [
  { id: 'photo', label: 'Foto de baixa qualidade ou inadequada', icon: '📸' },
  { id: 'name', label: 'Nome da loja com erros ou genérico', icon: '🏷️' },
  { id: 'whatsapp', label: 'Número de WhatsApp inválido/erro', icon: '📱' },
  { id: 'category', label: 'Categoria não condiz com os produtos', icon: '📁' },
  { id: 'description', label: 'Descrição muito curta ou confusa', icon: '📝' },
  { id: 'neighborhood', label: 'Bairro selecionado está incorreto', icon: '📍' },
];

const RequestChangesModal: React.FC<RequestChangesModalProps> = ({ vendor, onClose, onSend }) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [customMessage, setCustomMessage] = useState('');

  const toggleOption = (label: string) => {
    setSelectedOptions(prev => 
      prev.includes(label) ? prev.filter(o => o !== label) : [...prev, label]
    );
  };

  const handleConfirm = () => {
    let finalMessage = `Olá ${vendor.responsible}! 👋\n\nSou da equipe de moderação do Compre em Guarulhos.\n\nRecebemos seu cadastro da loja *${vendor.name}*, mas precisamos de alguns ajustes antes de aprová-lo:\n\n`;
    
    selectedOptions.forEach(opt => {
      finalMessage += `• ${opt}\n`;
    });

    if (customMessage.trim()) {
      finalMessage += `\n*Observações adicionais:* ${customMessage}\n`;
    }

    finalMessage += `\nPor favor, acesse seu painel e atualize essas informações para que possamos liberar sua vitrine! 🚀`;
    
    onSend(finalMessage);
  };

  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center p-4 sm:p-6 animate-in fade-in duration-300">
      <div className="absolute inset-0 bg-brand-navy/95 backdrop-blur-xl" onClick={onClose} />
      
      <div className="relative bg-white w-full max-w-2xl rounded-[3.5rem] shadow-2xl overflow-hidden animate-in zoom-in duration-300 flex flex-col max-h-[90vh]">
        <div className="p-10 border-b border-gray-50 flex items-center justify-between bg-gray-50/50">
          <div>
            <span className="text-brand-orange text-[10px] font-black uppercase tracking-[0.3em] mb-1 block">Feedback de Análise</span>
            <h3 className="text-2xl font-title font-black text-brand-navy uppercase italic tracking-tighter leading-none italic">Solicitar Alterações</h3>
          </div>
          <button onClick={onClose} className="p-4 text-gray-300 hover:text-brand-navy transition-colors">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>

        <div className="p-10 overflow-y-auto no-scrollbar flex-1 space-y-8">
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-6">Selecione os pontos de correção:</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {PRESET_OPTIONS.map(opt => (
                <div 
                  key={opt.id}
                  onClick={() => toggleOption(opt.label)}
                  className={`p-6 rounded-3xl border-2 transition-all cursor-pointer flex items-center gap-4 group ${
                    selectedOptions.includes(opt.label) 
                      ? 'border-brand-orange bg-brand-orange/5' 
                      : 'border-gray-100 bg-white hover:border-gray-200'
                  }`}
                >
                  <span className="text-2xl group-hover:scale-110 transition-transform">{opt.icon}</span>
                  <span className={`text-[10px] font-black uppercase leading-tight ${selectedOptions.includes(opt.label) ? 'text-brand-orange' : 'text-gray-500'}`}>
                    {opt.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-3">Mensagem Personalizada (Opcional):</label>
            <textarea 
              rows={4}
              placeholder="Ex: Por favor, remova o fundo da foto principal..."
              className="w-full p-6 rounded-3xl bg-inputGray border-none font-medium text-brand-navy focus:ring-2 focus:ring-brand-orange resize-none"
              value={customMessage}
              onChange={e => setCustomMessage(e.target.value)}
            />
          </div>
        </div>

        <div className="p-8 border-t border-gray-50 bg-gray-50/50 flex flex-col sm:flex-row gap-4">
          <button onClick={onClose} className="flex-1 bg-white border border-gray-200 text-gray-400 font-black py-5 rounded-2xl uppercase text-[10px] tracking-widest hover:bg-gray-100 transition-all">
            Cancelar
          </button>
          <button 
            disabled={selectedOptions.length === 0 && !customMessage.trim()}
            onClick={handleConfirm}
            className="flex-[2] bg-brand-navy text-white font-black py-5 rounded-2xl uppercase text-[10px] tracking-widest shadow-xl shadow-brand-navy/20 hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-30 disabled:pointer-events-none flex items-center justify-center gap-3"
          >
            <svg className="w-5 h-5 text-[#25D366]" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.72.937 3.659 1.432 5.632 1.433h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            Enviar via WhatsApp
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequestChangesModal;
